"use client";

import { useEffect, useState } from "react";

/**
 * Shown when /api/auth/callback sends someone back after a failed sign-in.
 *
 * Before this existed the callback redirected to `/?auth_error=1` and nothing
 * anywhere read the parameter, so a failed sign-in was indistinguishable from
 * clicking "home" — people were dropped on the homepage with no indication that
 * anything had gone wrong.
 *
 * Deliberately a client component reading `window.location`: the same pattern
 * app/programs/edit/page.tsx already uses, and it keeps the homepage statically
 * rendered. Reading searchParams in the server component would make the most
 * visited page on the site dynamic for the sake of a rare error banner.
 *
 * The message is intentionally vague. The specific reason is in the server log
 * (see logAuthFailure in the callback); telling a browser which check failed
 * tells an attacker the same thing.
 */
export function AuthErrorNotice() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("auth_error") === "1") {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  return (
    <div
      role="alert"
      style={{
        margin: "0 auto",
        maxWidth: "42rem",
        padding: "0.875rem 1rem",
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderLeft: "3px solid var(--error)",
        borderRadius: "var(--radius-md)",
        color: "var(--foreground)",
        fontSize: "0.9375rem",
      }}
    >
      <span style={{ flex: 1 }}>
        We couldn&rsquo;t sign you in. The link may have expired — please try again.
      </span>
      <button
        type="button"
        onClick={() => setVisible(false)}
        aria-label="Dismiss"
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "var(--muted)",
          fontSize: "1.125rem",
          lineHeight: 1,
          padding: "0.25rem",
        }}
      >
        &times;
      </button>
    </div>
  );
}
